import styled from '@emotion/styled';
import { memo } from 'react';
import { useTranslation } from 'react-i18next';
import { getMinutesAndSeconds } from '@hotjar/webapp/lib/getMinutesAndSeconds';
import { tokens, Text, Icon } from '@hotjar/hotsauce';
import { noop } from '@hotjar/webapp/lib/function';
import { moment } from '@hotjar/insights-common/lib/moment';

import type { PartialRecording } from '@hotjar/recordings/src/store/player/types';

const Prefix = styled.div`
  width: ${tokens.spacer.x5};
  text-align: center;
`;

const PlaybackIcon = styled(Icon)`
  color: ${tokens.BRAND_COLORS.cobalt_800};
`;

const PrefixText = styled(Text)``;

const PrefixHoverIcon = styled(Icon)`
  color: ${tokens.BRAND_COLORS.cobalt_800};
`;

const Container = styled.button<{ isActive: boolean; isWatched: boolean }>`
  display: flex;
  color: ${({ isWatched, isActive }) =>
    isWatched && !isActive
      ? tokens.ALIAS_TEXT_COLORS.text_disabled
      : tokens.ALIAS_TEXT_COLORS.text_default};
  align-items: center;
  height: ${tokens.spacer.x5};
  width: 100%;
  margin-bottom: ${tokens.spacer.x2};
  padding-right: ${tokens.spacer.x1pt5};
  border-radius: ${tokens.spacer.x0pt5};

  background-color: ${({ isActive }) =>
    isActive ? tokens.BRAND_COLORS.cobalt_200 : 'transparent'};
  border: ${({ isActive }) => (isActive ? `solid 1px ${tokens.BRAND_COLORS.cobalt_700}` : 'none')};

  ${PrefixHoverIcon} {
    display: none;
  }

  &:hover {
    background-color: ${tokens.BRAND_COLORS.cobalt_200};
    border-width: ${({ isActive }) => (isActive ? '1px' : '0')};
    cursor: ${({ isActive }) => (isActive ? 'default' : 'pointer')};
    color: ${tokens.ALIAS_TEXT_COLORS.text_default};

    ${PrefixText} {
      display: none;
    }

    ${PrefixHoverIcon} {
      display: inline-block;
    }
  }
`;

const CreationDate = styled(Text)`
  flex: 1;
  padding-right: ${tokens.spacer.x2};
  overflow: hidden;
  text-overflow: ellipsis;
  text-align: left;
`;

const epochDateShort = (epochTime: number): string => {
  return moment(epochTime * 1000).format('DD MMM, HH:mm');
};

type PlaylistItemParams = {
  recording: PartialRecording;
  index: number;
  isActive: boolean;
  isPlaying: boolean;
  isWatched: boolean;
  onClick: (id: number) => void;
};

const UnMemoPlaylistItem = ({
  recording,
  index,
  isActive,
  isPlaying,
  isWatched,
  onClick,
}: PlaylistItemParams): JSX.Element => {
  const { duration, date } = recording;
  const { t } = useTranslation();

  return (
    <Container
      isActive={isActive}
      isWatched={isWatched}
      data-testid="playlist-item"
      onClick={isActive ? noop : () => onClick(recording.id)}
    >
      <Prefix>
        {isActive ? (
          <PlaybackIcon
            icon={isPlaying ? 'pause_alt' : 'play_alt'}
            color="inherited"
            data-testid="playback-icon"
            description={
              isPlaying
                ? t('common:recordings.player.playlist.playing')
                : t('common:recordings.player.playlist.paused')
            }
          />
        ) : (
          <>
            <PrefixText textColor="inherited" data-testid="prefix-text">
              {index + 1}
            </PrefixText>
            <PrefixHoverIcon
              icon="play_alt"
              color="inherited"
              description={t('common:recordings.player.playlist.hovered')}
            />
          </>
        )}
      </Prefix>
      <CreationDate textColor="inherited">
        {date ? epochDateShort(date) : t('common:recordings.player.playlist.current-recording')}
      </CreationDate>
      {duration ? <Text textColor="inherited">{getMinutesAndSeconds(duration)}</Text> : null}
    </Container>
  );
};

export const PlaylistItem = memo(UnMemoPlaylistItem);
